import React, { useState, useEffect } from 'react';
import { 
  X, 
  Receipt, 
  Calendar, 
  User, 
  Table, 
  CreditCard, 
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  DollarSign,
  Hash,
  Package,
  ShoppingCart
} from 'lucide-react';
import { SaleDetailResource, SaleProductResource } from '../types';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface SaleDetailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  saleId: string;
}

const SaleDetailDialog: React.FC<SaleDetailDialogProps> = ({
  isOpen,
  onClose,
  saleId
}) => {
  const [saleDetail, setSaleDetail] = useState<SaleDetailResource | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && saleId) {
      loadSaleDetail();
    }
  }, [isOpen, saleId]);

  const loadSaleDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getSaleDetail(saleId);
      setSaleDetail(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sale details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      paid: 'bg-green-100 text-green-800',
      unpaid: 'bg-yellow-100 text-yellow-800',
      void: 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    const statusIcons: Record<string, React.ReactNode> = {
      paid: <CheckCircle className="h-4 w-4" />,
      unpaid: <Clock className="h-4 w-4" />,
      void: <XCircle className="h-4 w-4" />
    };
    return statusIcons[status] || <AlertCircle className="h-4 w-4" />;
  };

  const getPaymentMethodColor = (method: string) => {
    const methodColors: Record<string, string> = {
      cash: 'bg-green-100 text-green-800',
      debit: 'bg-blue-100 text-blue-800',
      online: 'bg-purple-100 text-purple-800',
      qris: 'bg-orange-100 text-orange-800'
    };
    return methodColors[method] || 'bg-gray-100 text-gray-800';
  };

  const renderProductOptions = (options: any) => {
    if (!options || typeof options !== 'object') return null;
    
    return (
      <div className="mt-2 text-xs text-gray-500">
        {Object.entries(options).map(([key, value]) => (
          <div key={key} className="flex justify-between">
            <span>{key}:</span>
            <span>{String(value)}</span>
          </div>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Sale Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <LoadingSpinner size="md" text="Loading sale details..." />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        ) : saleDetail ? (
          <div className="space-y-6">
            {/* Sale Header */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center mb-2">
                    <Receipt className="h-5 w-5 text-primary-600 mr-2" />
                    <h4 className="text-lg font-semibold text-gray-900 truncate">
                      {saleDetail.number}
                    </h4>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Hash className="h-4 w-4 mr-1" />
                      <span>#{saleDetail.queueNumber}</span>
                    </div>
                    {saleDetail.tableNumber && (
                      <div className="flex items-center">
                        <Table className="h-4 w-4 mr-1" />
                        <span>Table {saleDetail.tableNumber}</span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{formatDate(saleDetail.date)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-2 ml-4">
                  {/* Status Badge */}
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(saleDetail.status)}`}>
                    {getStatusIcon(saleDetail.status)}
                    <span className="ml-1 capitalize">{saleDetail.status}</span>
                  </span>
                  
                  {/* Payment Method */}
                  {saleDetail.paymentMethod && (
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodColor(saleDetail.paymentMethod)}`}>
                      <CreditCard className="h-3 w-3 mr-1" />
                      {saleDetail.paymentMethod}
                    </span>
                  )}
                </div>
              </div>

              {/* Customer Info */}
              {saleDetail.customerName && (
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <User className="h-4 w-4 mr-2 text-gray-400" />
                  <span>{saleDetail.customerName}</span>
                </div>
              )}

              {/* Note */}
              {saleDetail.note && (
                <p className="text-sm text-gray-600 italic">"{saleDetail.note}"</p>
              )}
            </div>

            {/* Products List */}
            <div>
              <h5 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                <Package className="h-5 w-5 mr-2 text-primary-600" />
                Products ({saleDetail.products?.length || 0} items)
              </h5>
              <div className="space-y-3">
                {saleDetail.products?.map((product) => (
                  <div key={product.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h6 className="font-medium text-gray-900">{product.productName}</h6>
                        {product.product.code && (
                          <p className="text-sm text-gray-500">Code: {product.product.code}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-gray-900">{formatCurrency(product.totalPrice)}</div>
                        <div className="text-sm text-gray-500">
                          {product.qty} × {formatCurrency(product.price)}
                        </div>
                      </div>
                    </div>
                    
                    {product.note && (
                      <p className="text-sm text-gray-600 italic mb-2">Note: {product.note}</p>
                    )}
                    
                    {renderProductOptions(product.options)}
                  </div>
                ))}
              </div>
            </div>

            {/* Financial Summary */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                <DollarSign className="h-5 w-5 mr-2 text-primary-600" />
                Financial Summary
              </h5>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium text-gray-900">{formatCurrency(saleDetail.totalAmount)}</span>
                </div>
                
                {saleDetail.discountAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Discount ({saleDetail.discountName || 'General'}):</span>
                    <span className="text-red-600">-{formatCurrency(saleDetail.discountAmount)}</span>
                  </div>
                )}
                
                {saleDetail.otherExpenseAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Other Expense ({saleDetail.otherExpenseName}):</span>
                    <span className="text-gray-600">+{formatCurrency(saleDetail.otherExpenseAmount)}</span>
                  </div>
                )}
                
                {saleDetail.ppnAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pajak ({saleDetail.ppnPercent}%):</span>
                    <span className="text-gray-600">+{formatCurrency(saleDetail.ppnAmount)}</span>
                  </div>
                )}
                
                {saleDetail.serviceChargeAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service Charge ({saleDetail.serviceChargeRate}%):</span>
                    <span className="text-gray-600">+{formatCurrency(saleDetail.serviceChargeAmount)}</span>
                  </div>
                )}
                
                {saleDetail.roundingAmount !== 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Rounding:</span>
                    <span className={saleDetail.roundingAmount > 0 ? 'text-gray-600' : 'text-red-600'}>
                      {saleDetail.roundingAmount > 0 ? '+' : ''}{formatCurrency(saleDetail.roundingAmount)}
                    </span>
                  </div>
                )}
                
                <div className="border-t border-gray-300 pt-2 mt-3">
                  <div className="flex justify-between font-semibold text-lg">
                    <span className="text-gray-900">Grand Total:</span>
                    <span className="text-green-600">{formatCurrency(saleDetail.grandTotal)}</span>
                  </div>
                </div>
                
                <div className="border-t border-gray-300 pt-2 mt-3 space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Paid Amount:</span>
                    <span className="font-medium text-green-600">{formatCurrency(saleDetail.paidAmount)}</span>
                  </div>
                  {saleDetail.changeAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Change:</span>
                      <span className="font-medium text-blue-600">{formatCurrency(saleDetail.changeAmount)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                <ShoppingCart className="h-5 w-5 mr-2 text-primary-600" />
                Additional Information
              </h5>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total Products:</span>
                  <span className="ml-2 font-medium">{saleDetail.totalQtyProduct} items</span>
                </div>
                {saleDetail.referenceNumber && (
                  <div>
                    <span className="text-gray-600">Reference:</span>
                    <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded text-xs">{saleDetail.referenceNumber}</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-600">Created:</span>
                  <span className="ml-2 font-medium">{formatDate(saleDetail.createdAt)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Updated:</span>
                  <span className="ml-2 font-medium">{formatDate(saleDetail.updatedAt)}</span>
                </div>
              </div>
            </div>

            {/* Company Branch Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                <Table className="h-5 w-5 mr-2 text-primary-600" />
                Branch Information
              </h5>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Branch:</span>
                  <span className="ml-2 font-medium">{saleDetail.companyBranch.name}</span>
                </div>
                <div>
                  <span className="text-gray-600">Branch Mode:</span>
                  <span className="ml-2 font-medium capitalize">{saleDetail.companyBranch.branchMode}</span>
                </div>
                {saleDetail.companyBranch.isMainBranch && (
                  <div className="col-span-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                      Main Branch
                    </span>
                  </div>
                )}
                {saleDetail.companyTable && (
                  <div>
                    <span className="text-gray-600">Table:</span>
                    <span className="ml-2 font-medium">{saleDetail.companyTable.name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Created By Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                <User className="h-5 w-5 mr-2 text-primary-600" />
                Created By
              </h5>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Name:</span>
                  <span className="ml-2 font-medium">{saleDetail.createdBy.fullName}</span>
                </div>
                <div>
                  <span className="text-gray-600">Email:</span>
                  <span className="ml-2 font-medium">{saleDetail.createdBy.email}</span>
                </div>
                <div>
                  <span className="text-gray-600">Role:</span>
                  <span className="ml-2 font-medium capitalize">{saleDetail.createdBy.userRole.replace('-', ' ')}</span>
                </div>
                {saleDetail.createdBy.phone && (
                  <div>
                    <span className="text-gray-600">Phone:</span>
                    <span className="ml-2 font-medium">{saleDetail.createdBy.phone}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Void Reason */}
            {saleDetail.status === 'void' && saleDetail.voidReason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Void Reason:</strong> {saleDetail.voidReason}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* Actions */}
        <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaleDetailDialog;
